import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { WhatsAppButton } from './WhatsAppButton';
import { ExternalLink, Globe, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';

const allPortfolioItems = [
  {
    company: 'YESFORT',
    name: 'App & Web Development',
    website: 'https://yesfort.com/de/home-de/',
    image: 'https://api.microlink.io/?url=https://yesfort.com/de/home-de/&screenshot=true&meta=false&embed=screenshot.url',
    industry: 'IT & Development',
    description: {
      en: 'Full-service IT company specializing in mobile app and website development.',
      fr: 'Entreprise IT spécialisée dans le développement d\'applications mobiles et de sites web.',
      ar: 'شركة تقنية متخصصة في تطوير تطبيقات الجوال والمواقع الإلكترونية.'
    }
  },
  {
    company: 'oFloor',
    name: 'Fugenlose Böden',
    website: 'https://www.o-floor.com/',
    image: 'https://api.microlink.io/?url=https://www.o-floor.com/&screenshot=true&meta=false&embed=screenshot.url',
    industry: 'Construction & Design',
    description: {
      en: 'Premium seamless floors and surfaces with over 20,000 color options.',
      fr: 'Sols et surfaces sans joints premium avec plus de 20 000 options de couleurs.',
      ar: 'أرضيات وأسطح بدون فواصل فاخرة مع أكثر من 20,000 خيار لون.'
    }
  },
  {
    company: 'SAND & SILICON',
    name: 'Electronic Components',
    website: 'https://sandsilicon.com/',
    image: 'https://customer-assets.emergentagent.com/job_cf89158a-bcc4-4183-9507-642ba4ecfd15/artifacts/l7826ofz_image.png',
    industry: 'Electronics Distribution',
    description: {
      en: 'Trusted distributor for electronic components and parts.',
      fr: 'Distributeur de confiance pour composants et pièces électroniques.',
      ar: 'موزع موثوق للمكونات والقطع الإلكترونية.'
    }
  },
  {
    company: 'Obrycki',
    name: 'Designerböden',
    website: 'https://obrycki.de/',
    image: 'https://api.microlink.io/?url=https://obrycki.de/&screenshot=true&meta=false&embed=screenshot.url',
    industry: 'Interior Design',
    description: {
      en: 'Seamless floors and surfaces for residential and commercial spaces.',
      fr: 'Sols et surfaces sans joints pour espaces résidentiels et commerciaux.',
      ar: 'أرضيات وأسطح بدون فواصل للمساحات السكنية والتجارية.'
    }
  },
  {
    company: 'SGF Rent Car',
    name: 'Car Rental Services',
    website: 'https://sgfrentcar.com/',
    image: 'https://customer-assets.emergentagent.com/job_cf89158a-bcc4-4183-9507-642ba4ecfd15/artifacts/0szatq0e_image.png',
    industry: 'Automotive',
    description: {
      en: 'Professional car rental services with a modern fleet.',
      fr: 'Services de location de voitures professionnels avec une flotte moderne.',
      ar: 'خدمات تأجير سيارات احترافية مع أسطول حديث.'
    }
  },
  {
    company: 'Eunde Personal',
    name: 'Personalvermittlung',
    website: 'https://eunde-personal.de/',
    image: 'https://api.microlink.io/?url=https://eunde-personal.de/&screenshot=true&meta=false&embed=screenshot.url',
    industry: 'HR & Recruitment',
    description: {
      en: 'Hotel services partner specializing in night audit and reception.',
      fr: 'Partenaire de services hôteliers spécialisé dans l\'audit de nuit et la réception.',
      ar: 'شريك خدمات فندقية متخصص في التدقيق الليلي والاستقبال.'
    }
  },
  {
    company: 'Abschleppdienst 24',
    name: 'Pannenhilfe & Abschleppen',
    website: 'https://www.abschleppdienst-24.com/',
    image: 'https://api.microlink.io/?url=https://www.abschleppdienst-24.com/&screenshot=true&meta=false&embed=screenshot.url',
    industry: 'Automotive Services',
    description: {
      en: '24/7 towing and roadside assistance services in Frankfurt.',
      fr: 'Services de remorquage et d\'assistance routière 24/7 à Francfort.',
      ar: 'خدمات السحب والمساعدة على الطريق على مدار الساعة في فرانكفورت.'
    }
  }
];

export const PortfolioPage = () => {
  const { t, language, isRTL } = useLanguage();

  return (
    <div className="min-h-screen bg-background" data-testid="portfolio-page">
      <Header />
      
      <main className="pt-16 md:pt-20">
        {/* Hero Section */}
        <section className="py-12 sm:py-16 md:py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
            <Link 
              to="/" 
              className={`inline-flex items-center gap-2 text-brand hover:underline mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <ArrowLeft className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
              {t('portfolio.backHome')}
            </Link>
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-brand/10 text-brand px-4 py-2 rounded-full mb-6">
                <Globe className="w-5 h-5" />
                <span className="font-medium">{t('portfolio.badge')}</span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
                {t('portfolio.title')}
              </h1>
              <p className="text-base sm:text-lg text-slate-600">
                {t('portfolio.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Portfolio Grid */}
        <section className="py-12 sm:py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {allPortfolioItems.map((item, index) => (
                <a
                  key={index}
                  href={item.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-slate-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                  data-testid={`portfolio-item-${index}`}
                >
                  {/* Website Screenshot */}
                  <div className="aspect-[16/10] overflow-hidden bg-slate-200 relative">
                    <img
                      src={item.image}
                      alt={`${item.company} Website`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden absolute inset-0 bg-slate-200 items-center justify-center">
                      <Globe className="w-12 h-12 text-slate-400" />
                    </div>
                    <div className="absolute inset-0 bg-brand/0 group-hover:bg-brand/10 transition-colors duration-300" />
                  </div>

                  {/* Company Info */}
                  <div className="p-5 sm:p-6">
                    <div className={`flex items-start justify-between gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className={`flex-1 ${isRTL ? 'text-right' : ''}`}>
                        <h3 className="font-bold text-slate-900 text-lg sm:text-xl group-hover:text-brand transition-colors mb-1">
                          {item.company}
                        </h3>
                        <p className="text-brand text-sm font-medium mb-2">{item.industry}</p>
                        <p className="text-slate-500 text-sm">
                          {item.description[language] || item.description.en}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="flex items-center gap-2 text-brand font-medium text-sm bg-brand/10 px-3 py-1.5 rounded-full group-hover:bg-brand group-hover:text-white transition-colors">
                          <ExternalLink className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-16 md:py-20 bg-slate-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
            <div className="max-w-2xl mx-auto space-y-6">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                {t('portfolio.cta.title')}
              </h2>
              <p className="text-slate-300 text-base sm:text-lg">
                {t('portfolio.cta.subtitle')}
              </p>
              <Link to="/#contact">
                <Button
                  className="h-12 sm:h-14 px-6 sm:px-8 rounded-full bg-brand hover:bg-brand/90 text-white text-base sm:text-lg font-semibold shadow-lg mt-4"
                >
                  {t('portfolio.cta.button')}
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default PortfolioPage;
